import React from 'react';
import { Users, MapPin, BookOpen, FileText, MessageSquare } from 'lucide-react';

const ProcesVerbalDetailsTab = ({ procesVerbal }) => {
  const getAllEtudiants = (soutenance) => {
    if (!soutenance) return [];
    
    const etudiants = [];
    
    if (soutenance.etudiants_soutenance && soutenance.etudiants_soutenance.length > 0) {
      soutenance.etudiants_soutenance.forEach(etudiantSoutenance => {
        if (etudiantSoutenance.etudiant) {
          etudiants.push(etudiantSoutenance.etudiant);
        }
      });
    } else if (soutenance.etudiant_principal_info) {
      etudiants.push(soutenance.etudiant_principal_info);
    }
    
    return etudiants;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non définie';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'Non définie';
    return timeString.slice(0, 5);
  };

  const getJuryInfo = (soutenance) => {
    if (!soutenance?.jury) return null;
    
    const jury = soutenance.jury;
    const president = jury.president_interne || jury.president_externe;
    
    // Récupérer les membres du jury par rôle
    const rapporteur = jury.membres_jury?.find(m => m.role === 'RAPPORTEUR');
    const examinateur = jury.membres_jury?.find(m => m.role === 'EXAMINATEUR');
    
    return {
      president: president ? {
        nom: president.nom || president.utilisateur?.last_name,
        prenom: president.prenom || president.utilisateur?.first_name
      } : null,
      rapporteur: rapporteur ? {
        nom: rapporteur.enseignant_interne?.nom || rapporteur.enseignant_externe?.utilisateur?.last_name,
        prenom: rapporteur.enseignant_interne?.prenom || rapporteur.enseignant_externe?.utilisateur?.first_name
      } : null,
      examinateur: examinateur ? {
        nom: examinateur.enseignant_interne?.nom || examinateur.enseignant_externe?.utilisateur?.last_name,
        prenom: examinateur.enseignant_interne?.prenom || examinateur.enseignant_externe?.utilisateur?.first_name
      } : null
    };
  };

  const etudiants = getAllEtudiants(procesVerbal?.soutenance);
  const soutenance = procesVerbal?.soutenance;
  const juryInfo = getJuryInfo(soutenance);

  return (
    <div className="print:hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Informations sur les étudiants */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <Users className="h-5 w-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Candidat(s)</h3>
          </div>
          <div className="space-y-4">
            {etudiants.length > 0 ? (
              etudiants.map((etudiant, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg">
                  <div className="font-semibold text-gray-900">
                    {etudiant.nom} {etudiant.prenom}
                  </div>
                  {etudiant.matricule && (
                    <div className="text-sm text-gray-600 mt-1">
                      Matricule: {etudiant.matricule}
                    </div>
                  )}
                  {etudiant.email && (
                    <div className="text-sm text-gray-600">
                      Email: {etudiant.email}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-gray-500">Aucun candidat spécifié</p>
            )}
          </div>
        </div>

        {/* Informations sur la soutenance */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <MapPin className="h-5 w-5 text-green-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Détails de la soutenance</h3>
          </div>
          <div className="space-y-3">
            <div>
              <span className="text-sm font-medium text-gray-600">Type:</span>
              <p className="text-gray-900">{soutenance?.type_soutenance?.nom || 'Non spécifié'}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Lieu:</span>
              <p className="text-gray-900">{soutenance?.salle?.nom || 'Non spécifié'}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Date et heure:</span>
              <p className="text-gray-900">
                {formatDate(soutenance?.date_soutenance)}
                {procesVerbal?.heure_debut && ` de ${formatTime(procesVerbal.heure_debut)}`}
                {procesVerbal?.heure_fin && ` à ${formatTime(procesVerbal.heure_fin)}`}
              </p>
            </div>
          </div>
        </div>

        {/* Titre du travail */}
        {soutenance?.titre_travail && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2">
            <div className="flex items-center mb-4">
              <BookOpen className="h-5 w-5 text-purple-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Titre du travail</h3>
            </div>
            <p className="text-gray-700 leading-relaxed">{soutenance.titre_travail}</p>
          </div>
        )}

        {/* Composition du jury */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <Users className="h-5 w-5 text-indigo-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Composition du jury</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-900">Président</div>
                <div className="text-sm text-gray-600">
                  {juryInfo?.president ? 
                    `${juryInfo.president.prenom} ${juryInfo.president.nom}` : 
                    'Non assigné'
                  }
                </div>
              </div>
              <div className="text-xs text-gray-500">
                {procesVerbal?.date_signature_president ? 
                  `Signé le ${formatDate(procesVerbal.date_signature_president)}` : 
                  'Non signé'
                }
              </div>
            </div>
            
            <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-900">Rapporteur</div>
                <div className="text-sm text-gray-600">
                  {juryInfo?.rapporteur ? 
                    `${juryInfo.rapporteur.prenom} ${juryInfo.rapporteur.nom}` : 
                    'Non assigné'
                  }
                </div>
              </div>
              <div className="text-xs text-gray-500">
                {procesVerbal?.date_signature_rapporteur ? 
                  `Signé le ${formatDate(procesVerbal.date_signature_rapporteur)}` : 
                  'Non signé'
                }
              </div>
            </div>
            
            <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-900">Examinateur</div>
                <div className="text-sm text-gray-600">
                  {juryInfo?.examinateur ? 
                    `${juryInfo.examinateur.prenom} ${juryInfo.examinateur.nom}` : 
                    'Non assigné'
                  }
                </div>
              </div>
              <div className="text-xs text-gray-500">
                {procesVerbal?.date_signature_examinateur ? 
                  `Signé le ${formatDate(procesVerbal.date_signature_examinateur)}` : 
                  'Non signé'
                }
              </div>
            </div>
          </div>
        </div>

        {/* Observations et recommandations */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <MessageSquare className="h-5 w-5 text-orange-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Observations du jury</h3>
          </div>
          
          {procesVerbal?.observations ? (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-600 mb-2">Observations:</h4>
              <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
                {procesVerbal.observations}
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-sm mb-4">Aucune observation</p>
          )}
          
          {procesVerbal?.recommandations ? (
            <div>
              <h4 className="text-sm font-medium text-gray-600 mb-2">Recommandations:</h4>
              <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
                {procesVerbal.recommandations}
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-sm">Aucune recommandation</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProcesVerbalDetailsTab; 